import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TIMEOUT_MS = 8000; // fail fast so tiles don't spin forever

// Tiny in-memory cache so the same reel doesn't refetch when the modal opens.
const cache = new Map();

/**
 * Resolve an Instagram reel to proxied thumbnail + video URLs served by our
 * FastAPI backend (bypasses IG's CORS / Referer / app-redirect issues).
 * Returns `{ status, thumb, video, title, uploader }`.
 *   status: "idle" | "loading" | "ok" | "fail"
 */
export default function useIgMedia(shortcode) {
  const [state, setState] = useState(() =>
    shortcode && cache.has(shortcode)
      ? cache.get(shortcode)
      : { status: shortcode ? "loading" : "idle" }
  );

  useEffect(() => {
    if (!shortcode) {
      setState({ status: "idle" });
      return;
    }
    if (cache.has(shortcode)) {
      setState(cache.get(shortcode));
      return;
    }

    let alive = true;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
    setState({ status: "loading" });

    fetch(`${API}/api/ig/${shortcode}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        clearTimeout(timer);
        const next = d.ok
          ? {
              status: "ok",
              thumb: d.thumb_url ? `${API}${d.thumb_url}` : null,
              video: d.video_url ? `${API}${d.video_url}` : null,
              title: d.title || "",
              uploader: d.uploader || "",
            }
          : { status: "fail" };
        cache.set(shortcode, next);
        if (alive) setState(next);
      })
      .catch(() => {
        clearTimeout(timer);
        const next = { status: "fail" };
        // Only cache negatives briefly (10s) so we retry naturally
        setTimeout(() => cache.delete(shortcode), 10_000);
        cache.set(shortcode, next);
        if (alive) setState(next);
      });

    return () => {
      alive = false;
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [shortcode]);

  return state;
}
